import React, { useState } from 'react';
import { API_CONFIG } from '../config/api.config';
import { ensureJsonResponse } from '../helpers/ensureJsonResponse';
import { sleep } from '../helpers/sleep.helper';
import { AnalysisResponse } from '../interfaces/analysisResponse';
import {
  ApplyCooldownRequest,
  ApplyCooldownResponse,
  GetUserReplyStatsResponse,
  ReanalysisResponse,
  ReanalyzeWithRepliesRequest,
  RemoveCooldownResponse,
  ReplyAnalysisResponse,
  ReplyInput,
  ReplyPreprocessResult,
  StorySafetyStats,
  UserMetadata,
  UseStoryAnalyzerReturn,
} from '../interfaces/postReply.interface';
import { SentimentResponse } from '../interfaces/sentimentResponse';
import {
  AnalyzeResultResponse,
  AnalyzeStatusResponse,
  AnalyzeStoryAsyncApiResponse,
} from '../interfaces/storyAnalysis.interface';
import { SentimentType } from '../types/Sentiment.type';

type MyAnalysis = { summary: string; topics: string[] };
type MySentiment = { score: number; label: SentimentType };
type AnalyzeRequest = { story: string };

// ─────────────────────────────────────────────────────────────────
// Async Reply Analysis Types (matching story analysis pattern)
// ─────────────────────────────────────────────────────────────────
interface AnalyzeReplyAsyncApiResponse {
  job_id: string;
  status: string;
}

interface AnalyzeReplyStatusResponse {
  job_id: string;
  status: string;
  message?: string;
  updated_at?: number;
  error_message?: string;
}

interface AnalyzeReplyResultResponse {
  job_id: string;
  status: string;
  result?: ReplyAnalysisResponse;
  error?: string;
}

export const useStoryAnalyzer = <
  TAnalysis = AnalysisResponse | ReplyAnalysisResponse,
  TSentiment = SentimentResponse,
>(): UseStoryAnalyzerReturn<TAnalysis, TSentiment> => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isAnalyzingReply, setIsAnalyzingReply] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<TAnalysis | null>(null);
  const [analyzeReplyResult, setAnalyzeReplyResult] = useState<Record<
    string,
    any
  > | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [analysisJobId, setAnalysisJobId] = useState<string | null>(null);
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [reanalysisResult, setReanalysisResult] =
    useState<ReanalysisResponse<TAnalysis> | null>(null);
  const [reanalysisError, setReanalysisError] = useState<string | null>(null);

  interface AnalyzeStoryProps {
    storyText: string;
    user_sources?: string[];
    verify_sources?: boolean;
  }

  const analyzeStory = React.useCallback(
    async (
      storyText: string,
      user_sources?: string[],
      verify_sources?: boolean,
    ): Promise<TAnalysis> => {
      setIsAnalyzing(true);
      setError(null);
      setAnalysisResult(null);

      const baseUrl = API_CONFIG.storyAnalyzer;

      try {
        // ─────────────────────────────────────────────
        // 1. SHORT STORIES → SYNC ENDPOINT
        // ─────────────────────────────────────────────
        if (storyText.length < 1500) {
          const resp = await fetch(`${baseUrl}/api/analyze-story`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              story: storyText,
              user_sources,
              verify_sources,
            } as AnalyzeRequest),
          });

          if (!resp.ok) {
            let message = 'Analysis failed';
            try {
              const errJson = await resp.json();
              if (
                errJson &&
                typeof errJson === 'object' &&
                'error' in errJson
              ) {
                message = (errJson as any).error ?? message;
              }
            } catch {
              /* ignore parse error */
            }
            throw new Error(message);
          }

          const result = await ensureJsonResponse<TAnalysis>(
            resp,
            'sync analysis',
          );

          setAnalysisResult(result);
          return result;
        }

        // ─────────────────────────────────────────────
        // 2. LONG STORIES → ASYNC JOB FLOW
        // ─────────────────────────────────────────────

        // 2.1 Start async job
        const startResp = await fetch(`${baseUrl}/api/analyze-story-async`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            story: storyText,
            user_sources,
            verify_sources,
          } as AnalyzeRequest),
        });

        if (!startResp.ok) {
          let message = 'Analysis failed';
          try {
            const errJson = await startResp.json();
            if (errJson && typeof errJson === 'object' && 'error' in errJson) {
              message = (errJson as any).error ?? message;
            }
          } catch {
            /* ignore parse error */
          }
          throw new Error(message);
        }

        const startPayload =
          await ensureJsonResponse<AnalyzeStoryAsyncApiResponse>(
            startResp,
            'async job creation',
          );

        const jobId = startPayload.job_id;
        if (!jobId) {
          throw new Error('Analyze API did not return a job_id');
        }
        setAnalysisJobId(jobId);
        // 2.2 Poll status
        const TIMEOUT_MINUTES = 15;
        const pollIntervalMs = 3_000;
        const timeoutMs = TIMEOUT_MINUTES * 60_000;
        const startTime = Date.now();

        while (true) {
          if (Date.now() - startTime > timeoutMs) {
            throw new Error('Analysis timed out');
          }

          const statusResp = await fetch(
            `${baseUrl}/api/analyze-job-status/${jobId}`,
            { method: 'GET' },
          );

          if (!statusResp.ok) {
            throw new Error(`Status check failed: ${statusResp.status}`);
          }

          const statusData = await ensureJsonResponse<AnalyzeStatusResponse>(
            statusResp,
            'job status',
          );

          const status = (statusData.status ?? '').toString().toUpperCase();

          if (status === 'COMPLETE') {
            break;
          }

          if (
            status === 'FAILED' ||
            status === 'EXPIRED' ||
            status === 'ERROR'
          ) {
            throw new Error(
              statusData.error_message ||
                `Analysis failed with status ${status}`,
            );
          }

          await sleep(pollIntervalMs);
        }

        // 2.3 Fetch final result
        const resultResp = await fetch(
          `${baseUrl}/api/analyze-job-result/${jobId}`,
          { method: 'GET' },
        );

        if (!resultResp.ok) {
          throw new Error(`Result fetch failed: ${resultResp.status}`);
        }

        const resultPayload = await ensureJsonResponse<
          AnalyzeResultResponse<TAnalysis>
        >(resultResp, 'job result');

        const finalResult = resultPayload.result as TAnalysis | undefined;
        if (finalResult === undefined) {
          console.error('Result payload missing .result:', resultPayload);
          throw new Error('Analysis result is missing in the response');
        }

        setAnalysisResult(finalResult as TAnalysis);
        return finalResult as TAnalysis;
      } catch (err: any) {
        console.error('Analysis error:', err);
        setError(err?.message ?? 'Analysis failed');
        throw err;
      } finally {
        setIsAnalyzing(false);
      }
    },
    [],
  );

  const getSentiment = React.useCallback(async (storyText: string) => {
    try {
      const response = await fetch(
        `${API_CONFIG.storyAnalyzer}/api/get-sentiment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ story: storyText }),
        },
      );

      if (!response.ok) {
        throw new Error('Sentiment analysis failed');
      }

      return await response.json();
    } catch (err) {
      console.error('Sentiment analysis error:', err);
      return null;
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // Analyze a REPLY before posting (ML-based)
  // Now supports both SYNC (short replies) and ASYNC (long replies)
  // Mirrors the analyzeStory pattern exactly
  // ─────────────────────────────────────────────────────────────────
  const analyzeReply = React.useCallback(
    async (
      replyText: string,
      userId?: string,
      storyId?: string,
      options?: {
        username?: string;
        reputationScore?: number;
        parentStoryId?: string;
      },
    ): Promise<ReplyAnalysisResponse | null> => {
      setIsAnalyzingReply(true);
      setError(null);
      setAnalyzeReplyResult(null);

      const baseUrl = API_CONFIG.storyAnalyzer;

      // Build request body (same for sync and async)
      const requestBody = {
        reply_text: replyText,
        user_id: userId ?? 'anonymous',
        story_id: storyId ?? 'unknown',
        username: options?.username,
        reputation_score: options?.reputationScore ?? 0.5,
        parent_story_id: options?.parentStoryId,
      };

      try {
        // ─────────────────────────────────────────────
        // 1. SHORT REPLIES → SYNC ENDPOINT
        // ─────────────────────────────────────────────
        if (replyText.length < 1000) {
          const response = await fetch(`${baseUrl}/api/replies/analyze`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
          });

          if (!response.ok) {
            let message = 'Reply analysis failed';
            try {
              const errJson = await response.json();
              if (
                errJson &&
                typeof errJson === 'object' &&
                'error' in errJson
              ) {
                message = (errJson as any).error ?? message;
              }
            } catch {
              /* ignore parse error */
            }
            throw new Error(message);
          }

          const result = await ensureJsonResponse<ReplyAnalysisResponse>(
            response,
            'reply analysis',
          );

          const finalResult = {
            analysis: result.analysis,
            canPublish: result.can_publish,
            overallScore: result.analysis?.overall_score,
            toxicity: result.analysis?.toxicity?.TOXIC,
            warningCount: result.warnings?.length ?? 0,
            rateLimited: result.rate_limit_info?.blocked ?? false,
          };

          // Log warnings if any
          if (result.warnings && result.warnings.length > 0) {
            console.warn('⚠️ Reply warnings:', result.warnings);
          }

          setAnalyzeReplyResult(finalResult);
          return result;
        }

        // ─────────────────────────────────────────────
        // 2. LONG REPLIES → ASYNC JOB FLOW
        // ─────────────────────────────────────────────
        // 2.1 Start async job
        const startResp = await fetch(`${baseUrl}/api/replies/analyze-async`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });

        if (!startResp.ok) {
          let message = 'Reply analysis failed';
          try {
            const errJson = await startResp.json();
            if (errJson && typeof errJson === 'object' && 'error' in errJson) {
              message = (errJson as any).error ?? message;
            }
          } catch {
            /* ignore parse error */
          }
          throw new Error(message);
        }

        const startPayload =
          await ensureJsonResponse<AnalyzeReplyAsyncApiResponse>(
            startResp,
            'async reply job creation',
          );

        const jobId = startPayload.job_id;
        if (!jobId) {
          console.error(
            'Async reply analyze response had no job_id:',
            startPayload,
          );
          throw new Error('Reply analyze API did not return a job_id');
        }

        // 2.2 Poll status
        const TIMEOUT_MINUTES = 5; // Replies should be faster than stories
        const pollIntervalMs = 2_000; // Poll every 2 seconds
        const timeoutMs = TIMEOUT_MINUTES * 60_000;
        const startTime = Date.now();

        while (true) {
          if (Date.now() - startTime > timeoutMs) {
            throw new Error('Reply analysis timed out');
          }

          const statusResp = await fetch(
            `${baseUrl}/api/replies/analyze-job-status/${jobId}`,
            { method: 'GET' },
          );

          if (!statusResp.ok) {
            throw new Error(`Reply status check failed: ${statusResp.status}`);
          }

          const statusData =
            await ensureJsonResponse<AnalyzeReplyStatusResponse>(
              statusResp,
              'reply job status',
            );

          const status = (statusData.status ?? '').toString().toUpperCase();

          if (status === 'COMPLETE') {
            break;
          }

          if (
            status === 'FAILED' ||
            status === 'EXPIRED' ||
            status === 'ERROR'
          ) {
            throw new Error(
              statusData.error_message ||
                statusData.message ||
                `Reply analysis failed with status ${status}`,
            );
          }

          await sleep(pollIntervalMs);
        }

        // 2.3 Fetch final result
        const resultResp = await fetch(
          `${baseUrl}/api/replies/analyze-job-result/${jobId}`,
          { method: 'GET' },
        );

        if (!resultResp.ok) {
          throw new Error(`Reply result fetch failed: ${resultResp.status}`);
        }

        const resultPayload =
          await ensureJsonResponse<AnalyzeReplyResultResponse>(
            resultResp,
            'reply job result',
          );

        // The result is nested inside resultPayload.result
        const result = resultPayload.result;
        if (!result) {
          console.error('Reply result payload missing .result:', resultPayload);
          throw new Error('Reply analysis result is missing in the response');
        }

        const finalResult = {
          analysis: result.analysis,
          canPublish: result.can_publish,
          overallScore: result.analysis?.overall_score,
          toxicity: result.analysis?.toxicity?.TOXIC,
          warningCount: result.warnings?.length ?? 0,
          rateLimited: result.rate_limit_info?.blocked ?? false,
        };

        // Log warnings if any
        if (result.warnings && result.warnings.length > 0) {
          console.warn('⚠️ Reply warnings:', result.warnings);
        }

        // @ts-ignore
        setAnalyzeReplyResult(finalResult);
        return result;
      } catch (err: any) {
        console.error('Reply analysis error:', err);
        setError(err?.message ?? 'Reply analysis failed');
        return null;
      } finally {
        setIsAnalyzingReply(false);
      }
    },
    [],
  );

  // ─────────────────────────────────────────────────────────────────
  // https://eagleofthesun.atlassian.net/browse/TEX-279
  // Re-analyze story with replies (BLENDING - different from analyzeReply)
  const reanalyzeWithReplies = React.useCallback(
    async (
      originalStory: string,
      originalAnalysis: Record<string, unknown>,
      replies: ReplyInput[],
      storyId?: string,
      user_sources?: string[],
      verify_sources: boolean = false,
    ): Promise<ReanalysisResponse<TAnalysis>> => {
      setIsReanalyzing(true);
      setReanalysisError(null);
      setReanalysisResult(null);

      const baseUrl = API_CONFIG.storyAnalyzer;

      try {
        const requestBody: ReanalyzeWithRepliesRequest = {
          original_story: originalStory,
          original_analysis: originalAnalysis,
          replies,
          story_id: storyId,
          user_sources: user_sources ?? null,
          verify_sources,
        };

        const response = await fetch(`${baseUrl}/api/replies/reanalyze`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          let message = 'Re-analysis failed';
          let safetyFailures = [];

          try {
            const errJson = await response.json();
            if (errJson && typeof errJson === 'object') {
              if ('error' in errJson) {
                message = (errJson as any).error ?? message;
              }
              if ('safety_failures' in errJson) {
                safetyFailures = (errJson as any).safety_failures;
              }
            }
          } catch {
            /* ignore parse error */
          }

          // If rate limited (429), include more context
          if (response.status === 429 && safetyFailures.length > 0) {
            throw new Error(
              `Rate limited: ${message}. ${safetyFailures.length} replies blocked.`,
            );
          }

          throw new Error(message);
        }

        const result = await ensureJsonResponse<ReanalysisResponse<TAnalysis>>(
          response,
          'reanalysis',
        );

        // Log any safety failures
        if (result.safety_failures && result.safety_failures.length > 0) {
          console.warn('⚠️ Some replies were blocked:', result.safety_failures);
        }

        setReanalysisResult(result);
        return result;
      } catch (err: any) {
        console.error('Re-analysis error:', err);
        setReanalysisError(err?.message ?? 'Re-analysis failed');
        throw err;
      } finally {
        setIsReanalyzing(false);
      }
    },
    [],
  );

  // Preprocess a single reply (for preview/validation before submit)
  const preprocessReply = React.useCallback(
    async (
      replyText: string,
      userId?: string,
      storyId?: string,
      userMetadata?: UserMetadata,
    ): Promise<ReplyPreprocessResult | null> => {
      const baseUrl = API_CONFIG.storyAnalyzer;

      try {
        const response = await fetch(`${baseUrl}/api/replies/preprocess`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: replyText,
            user_id: userId ?? 'anonymous',
            story_id: storyId ?? 'unknown',
            user_metadata: userMetadata ?? {},
          }),
        });

        if (!response.ok) {
          let message = 'Reply preprocessing failed';
          try {
            const errJson = await response.json();
            if (errJson && typeof errJson === 'object' && 'error' in errJson) {
              message = (errJson as any).error ?? message;
            }
          } catch {
            /* ignore parse error */
          }
          throw new Error(message);
        }

        const result = await ensureJsonResponse<ReplyPreprocessResult>(
          response,
          'reply preprocessing',
        );

        // Warn if rate limited
        if (result.rate_limit_info?.blocked) {
          console.warn(
            '⚠️ Rate limit would block this reply:',
            result.rate_limit_info.reason,
          );
        }

        return result;
      } catch (err) {
        console.error('Reply preprocessing error:', err);
        return null;
      }
    },
    [],
  );

  // Get user rate limit stats
  const getUserReplyStats = React.useCallback(
    async (userId: string): Promise<GetUserReplyStatsResponse | null> => {
      const baseUrl = API_CONFIG.storyAnalyzer;

      try {
        const response = await fetch(
          `${baseUrl}/api/replies/user-stats/${userId}`,
          {
            method: 'GET',
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to get user stats: ${response.status}`);
        }

        return await response.json();
      } catch (err) {
        console.error('Error getting user reply stats:', err);
        return null;
      }
    },
    [],
  );

  // Get story safety stats
  const getStorySafetyStats = React.useCallback(
    async (storyId: string): Promise<StorySafetyStats | null> => {
      const baseUrl = API_CONFIG.storyAnalyzer;

      try {
        const response = await fetch(
          `${baseUrl}/api/replies/story-stats/${storyId}`,
          {
            method: 'GET',
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to get story stats: ${response.status}`);
        }

        return await response.json();
      } catch (err) {
        console.error('Error getting story safety stats:', err);
        return null;
      }
    },
    [],
  );

  // ─────────────────────────────────────────────────────────────────
  // ADMIN: Apply cooldown to a user
  // POST /api/replies/admin/apply-cooldown
  // ─────────────────────────────────────────────────────────────────
  const applyUserCooldown = React.useCallback(
    async (
      userId: string,
      reason: string,
      durationMinutes?: number,
    ): Promise<ApplyCooldownResponse | null> => {
      const baseUrl = API_CONFIG.storyAnalyzer;

      try {
        const requestBody: ApplyCooldownRequest = {
          user_id: userId,
          reason,
          duration_minutes: durationMinutes, // Optional: uses server default if not provided
        };

        const response = await fetch(
          `${baseUrl}/api/replies/admin/apply-cooldown`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
          },
        );

        if (!response.ok) {
          let message = 'Failed to apply cooldown';
          try {
            const errJson = await response.json();
            if (errJson && typeof errJson === 'object' && 'error' in errJson) {
              message = (errJson as any).error ?? message;
            }
          } catch {
            /* ignore parse error */
          }
          throw new Error(message);
        }

        const result = await ensureJsonResponse<ApplyCooldownResponse>(
          response,
          'apply cooldown',
        );
        return result;
      } catch (err) {
        console.error('Error applying cooldown:', err);
        return null;
      }
    },
    [],
  );

  // ─────────────────────────────────────────────────────────────────
  // ADMIN: Remove cooldown from a user
  // POST /api/replies/admin/remove-cooldown
  // ─────────────────────────────────────────────────────────────────
  const removeUserCooldown = React.useCallback(
    async (userId: string): Promise<RemoveCooldownResponse | null> => {
      const baseUrl = API_CONFIG.storyAnalyzer;

      try {
        const response = await fetch(
          `${baseUrl}/api/replies/admin/remove-cooldown`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ user_id: userId }),
          },
        );

        if (!response.ok) {
          let message = 'Failed to remove cooldown';
          try {
            const errJson = await response.json();
            if (errJson && typeof errJson === 'object' && 'error' in errJson) {
              message = (errJson as any).error ?? message;
            }
          } catch {
            /* ignore parse error */
          }
          throw new Error(message);
        }

        const result = await ensureJsonResponse<RemoveCooldownResponse>(
          response,
          'remove cooldown',
        );

        return result;
      } catch (err) {
        console.error('Error removing cooldown:', err);
        return null;
      }
    },
    [],
  );

  const testConnection = async () => {
    try {
      const response = await fetch(`${API_CONFIG.storyAnalyzer}/api/health`);
      const data = await response.json();
      return true;
    } catch (err) {
      console.error('❌ Cannot connect to API:', err);
      setError('Cannot connect to API. Make sure the server is running.');
      return false;
    }
  };

  const getTestStories = async () => {
    try {
      const response = await fetch(
        `${API_CONFIG.storyAnalyzer}/api/test-stories`,
      );
      const data = await response.json();
      return data.test_stories;
    } catch (err) {
      console.error('❌ Error getting test stories:', err);
      return [];
    }
  };

  const clearResults = React.useCallback(() => {
    setAnalysisResult(null);
    setError(null);
  }, []);

  // Clear re-analysis results https://eagleofthesun.atlassian.net/browse/TEX-279
  const clearReanalysisResults = React.useCallback(() => {
    setReanalysisResult(null);
    setReanalysisError(null);
  }, []);

  return {
    analyzeStory,
    getSentiment,
    isAnalyzing,
    analysisResult,
    analysisJobId,
    error,
    testConnection,
    getTestStories,
    clearResults,
    // Reply analysis (ML-based, before posting)
    analyzeReply,
    analyzeReplyResult,
    isAnalyzingReply,
    // Reply re-analysis (blending story score with replies)
    reanalyzeWithReplies,
    preprocessReply,
    isReanalyzing,
    reanalysisResult,
    reanalysisError,
    clearReanalysisResults,
    // Rate limit and safety stats
    getUserReplyStats,
    getStorySafetyStats,
    // Admin cooldown management
    applyUserCooldown,
    removeUserCooldown,
  };
};
